import {
    IBurstDataChild,
    ISpectrumChartResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { mapBurstDataToPoints } from 'pages/workbench/workbench_tabs/trend/frequency_domain/shared';
import { AxisChartSeriesData } from 'shared/chart/core/types';
import { queryClient } from 'utils/query';

const queryKey = 'frequency-domain-burst-data';

export function invalidateFrequencyDomainData() {
    queryClient.invalidateQueries({
        queryKey: [queryKey],
    });
}

type UseFrequencyDomainDataDataOptions = {
    includeBaseline?: boolean;
    returnVelocity?: boolean;
};

export type SeriesData = Pick<
    AxisChartSeriesData,
    'dataPoints' | 'seriesMaxY' | 'seriesMinY' | 'seriesUnit' | 'seriesId'
>;

export type FrequencyDomainData = Readonly<{
    frequencySeriesData: SeriesData;
    baselineSeriesData: SeriesData | undefined;
    isVibrationType: boolean;
}>;

function mapSeries(data: IBurstDataChild, seriesId: string): SeriesData {
    const dataPoints = mapBurstDataToPoints(data);
    const mappedYs = dataPoints.map(({ y }) => y);

    return {
        seriesId,
        seriesUnit: data.Units,
        seriesMinY: Math.min(...mappedYs),
        seriesMaxY: Math.max(...mappedYs),
        dataPoints,
    };
}

function mapFrequencyData(
    data: ISpectrumChartResponse,
    indicatorId: string
): FrequencyDomainData {
    const frequencySeriesData = mapSeries(data.BurstData[0], indicatorId);

    const baselineSeriesData = data.BurstData[1]
        ? mapSeries(data.BurstData[1], `${indicatorId}-baseline`)
        : undefined;

    const isVibrationType = data.IsVibrationTypeIndicator;

    return { frequencySeriesData, baselineSeriesData, isVibrationType };
}

export function useFrequencyDomainData(
    indicatorId: string,
    time: Date,
    options?: UseFrequencyDomainDataDataOptions
) {
    return useQuery({
        queryKey: [queryKey, indicatorId, time, options],

        queryFn: () =>
            sdClient.getBurstData({
                indicatorId,
                includeBaseline: options?.includeBaseline ?? false,
                returnTimeDomain: false,
                returnVelocity: options?.returnVelocity ?? false,
                dateTimestamp: time,
            }),

        select: (data) => mapFrequencyData(data, indicatorId),
    });
}
